import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { base64ToImage } from './VerbatimUtils';

const normalizeColumnName = (column) => String(column ?? '').trim().toLowerCase();

const sanitizeSheetName = (sheetName) => (
  String(sheetName ?? '')
    .replace(/[:\\/?*\[\]]/g, '-')
    .slice(0, 31)
);

const sanitizeFileName = (fileName, extension = '.xlsx') => {
	const normalizedExtension = extension.startsWith('.') ? extension : `.${extension}`;
	const sanitizedBaseName = String(fileName ?? '')
		.replace(/[<>:"/\\|?*]/g, '_')
		.replace(/\s+/g, '_')
		.replace(/_+/g, '_')
		.replace(/[._]+$/g, '')
		.slice(0, 150)
		.replace(/[._]+$/g, '');

	return `${sanitizedBaseName || 'export'}${normalizedExtension}`;
};

const RADARLY_FORMATS = [
	{
		key: 'current',
		label: 'Current Radarly',
		requiredColumns: ['text', 'permalink'],
		contentColumns: ['text'],
		linkColumn: 'permalink',
		sentimentColumn: 'tone',
		reservedColumns: ['text', 'permalink'],
	},
	{
		key: 'legacy',
		label: 'Legacy',
		requiredColumns: ['hit sentence', 'opening text', 'url'],
		contentColumns: ['hit sentence', 'opening text'],
		linkColumn: 'url',
		sentimentColumn: 'sentiment',
		reservedColumns: ['hit sentence', 'opening text', 'url'],
	},
];

const isRadarlyReservedColumn = (column, format) => (
	(format?.reservedColumns || []).includes(normalizeColumnName(column))
);

const isEmptyExcelRow = (row) => (
	!Array.isArray(row)
	|| row.every((value) => value === undefined || value === null || (typeof value === 'string' && value.trim() === ''))
);

const toExcelString = (value) => {
	if (typeof value === 'string') return value.trim();
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	return '';
};

const toExcelBoolean = (value, fallback = false) => {
	if (typeof value === 'boolean') return value;
	if (typeof value !== 'string') return fallback;

	const normalizedValue = value.trim().toLowerCase();
	if (normalizedValue === 'true') return true;
	if (normalizedValue === 'false') return false;
	return fallback;
};

const findHeaderIndex = (headers, expectedHeader) => (
	headers.findIndex((header) => normalizeColumnName(header) === normalizeColumnName(expectedHeader))
);

export const prepareRadarlyExcelImport = (worksheet) => {
	if (!worksheet?.['!ref']) {
		throw new Error('The Excel sheet is empty.');
	}

	const worksheetRange = XLSX.utils.decode_range(worksheet['!ref']);
	const lastHeaderCandidateRow = Math.min(worksheetRange.e.r, worksheetRange.s.r + 19);
	let detectedFormat = null;
	let headerRowIndex = -1;

	for (let rowIndex = worksheetRange.s.r; rowIndex <= lastHeaderCandidateRow; rowIndex += 1) {
		const normalizedHeaders = new Set();

		for (let columnIndex = worksheetRange.s.c; columnIndex <= worksheetRange.e.c; columnIndex += 1) {
			const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
			const normalizedHeader = normalizeColumnName(worksheet[cellAddress]?.v);

			if (normalizedHeader) {
				normalizedHeaders.add(normalizedHeader);
			}
		}

		detectedFormat = RADARLY_FORMATS.find((format) => (
			format.requiredColumns.every((column) => normalizedHeaders.has(column))
		));

		if (detectedFormat) {
			headerRowIndex = rowIndex;
			break;
		}
	}

	if (!detectedFormat || headerRowIndex < 0) {
		throw new Error(
			'The Excel format could not be recognized. Expected Text/Permalink or Hit Sentence/Opening Text/URL columns.'
		);
	}

	const excelData = XLSX.utils.sheet_to_json(worksheet, {
		header: 1,
		range: headerRowIndex,
		defval: '',
	});
	const headers = Array.isArray(excelData[0]) ? excelData[0] : [];
	const columns = headers.filter((column) => (
		normalizeColumnName(column) !== ''
		&& !isRadarlyReservedColumn(column, detectedFormat)
	));

	return {
		excelData,
		columns,
		format: detectedFormat,
		headerRowNumber: headerRowIndex + 1,
	};
};

/**
 * Exports chart data to an Excel file with sample size and applied filters.
 * @param {string} labelType - The label type of the chart data.
 * @param {Object} chartData - The chart data object.
 * @param {Object} sampleSizes - Sample sizes associated with each label type.
 * @param {Object} labelTypes - Label types with sentiment-enabled information.
 * @param {Object} filters - Filters applied to the chart data.
 * @param {boolean} showPercentages - Whether to show data as percentages or raw counts.
 * @param {boolean} showMixedSentiment - Whether to show data as mixed sentiment.
 */

/**
 * Export the pivot table data to an Excel file, including metadata.
 *
 * @param {Object} pivotData - The pivot table data object.
 * @param {Array} rowLabels - Array of row labels.
 * @param {Array} colLabels - Array of column labels.
 * @param {string} selectedRowLabel - Name of the row label type.
 * @param {string} selectedColLabel - Name of the column label type.
 * @param {string} fileName - Name of the Excel file to be created.
 * @param {boolean} isPercentage - Whether the data is a percentage (true) or raw values (false).
 * @param {number} sampleSize - The sample size of the data.
 * @param {string} displayMode - The type of calculation applied (e.g., raw, percentage, zscore, etc.).
 */

export const exportChartDataToExcel = (labelType, chartData, sampleSizes, labelTypes, filters, showPercentages, showMixedSentiment) => {
  if (!chartData || !chartData[labelType]) {
    console.error('chartData or chartData[labelType] is undefined');
    alert(`No chart data available for ${labelType}`);
    return;
  }

  const labelCounts = chartData[labelType];
  const isSentimentEnabled = labelTypes?.[labelType]?.sentimentEnabled || false;
  const sampleSize = sampleSizes[labelType] || 0;

  // Helper function to format numbers for Excel
  const formatValue = (value) => {
    if (showPercentages) {
      const numericValue = typeof value === 'number' ? value : (parseFloat(value) || 0);
      // Divide by 100 to convert percentage to decimal for Excel
      return parseFloat((numericValue / 100).toFixed(4));
    }
    return value || 0;
  };

  // Prepare chart data rows
  const dataRows = Object.entries(labelCounts).map(([label, counts]) => {
    if (isSentimentEnabled && labelType !== 'Sentiment') {
      if (showMixedSentiment) {
        return {
          Label: label,
          [`Positive${showPercentages ? ' (%)' : ''}`]: formatValue(counts[0]),
          [`Negative/Mixed${showPercentages ? ' (%)' : ''}`]: formatValue(counts[1]),
          [`Neutral${showPercentages ? ' (%)' : ''}`]: formatValue(counts[3]),
        };
      } else {
        return {
          Label: label,
          [`Positive${showPercentages ? ' (%)' : ''}`]: formatValue(counts[0]),
          [`Negative${showPercentages ? ' (%)' : ''}`]: formatValue(counts[1]),
          [`Mixed${showPercentages ? ' (%)' : ''}`]: formatValue(counts[2]),
          [`Neutral${showPercentages ? ' (%)' : ''}`]: formatValue(counts[3]),
        };
      }
    } else {
      return {
        Label: label,
        [`Count${showPercentages ? ' (%)' : ''}`]: formatValue(counts[0]),
      };
    }
  });

  if (dataRows.length === 0) {
    alert(`No data available to export for ${labelType}`);
    return;
  }

  // Convert filters object to a readable format for the export
  const appliedFilters = Object.entries(filters.dynamicFilters)
    .filter(([, values]) => values.length > 0)
    .map(([filterName, values]) => `${filterName}: ${values.join(', ')}`)
    .join('; ');

  // Add metadata rows for sample size and filters at the bottom
  const metadataRows = [
    {}, // Blank row as a separator
    { Label: `Sample Size: ${sampleSize}` },
    { Label: `Applied Filters: ${appliedFilters || 'None'}` },
  ];

  // Combine data and metadata rows for export
  const worksheetData = [...dataRows, ...metadataRows];
  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  // Apply percentage formatting if needed
  if (showPercentages) {
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    // Loop through all data cells (excluding header row and metadata)
    for (let R = 1; R < dataRows.length + 1; R++) {
      for (let C = 1; C <= range.e.c; C++) { // start from column 1 to skip 'Label' column
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellAddress];
        
        if (cell && typeof cell.v === 'number') {
          // Define the cell format as percentage
          cell.z = '0.00%';
        }
      }
    }
  }
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(labelType));

  // Export the file with a meaningful filename
  XLSX.writeFile(
		workbook,
		sanitizeFileName(`${labelType}_chart_data_export`)
	);
};

export const exportToXLSX = (filename, data) => {
  // Ensure data.labels and data.labelTypes are defined
  const labels = data?.labels || {};
  const labelTypes = data?.labelTypes || {};

  // Labels sheet headers
  const labelsHeaders = ['Label', 'Type', 'Keywords', 'Explanation'];
  const formattedLabelsData = [labelsHeaders];

  // Populate the labels data (Label, Type, Keywords, Explanation)
  Object.entries(labels).forEach(([labelType, dataArray]) => {
    if (!['Gender', 'Age', 'Sentiment', 'Sources'].includes(labelType)) { // Exclude Gender, Age, Sentiment
      if (Array.isArray(dataArray)) {
        // If it's an array (e.g., labels under a type), iterate through the items
        dataArray.forEach((labelObj) => {
          const labelName = labelObj.name || labelType; // Use 'name' if present, fallback to labelType
          const keywords = Array.isArray(labelObj.keywords) ? labelObj.keywords.join('; ') : '';
          const explanation = labelObj.explanation || ''; // Include explanation if present
          const row = [labelName, labelObj.type || labelType, keywords, explanation];
          formattedLabelsData.push(row);
        });
      } else {
        // Process individual labels directly if not an array
        const keywords = Array.isArray(dataArray.keywords) ? dataArray.keywords.join('; ') : '';
        const explanation = dataArray.explanation || ''; // Include explanation if present
        const row = [labelType, dataArray.type, keywords, explanation];
        formattedLabelsData.push(row);
      }
    }
  });

  // Type Parameters sheet headers
  const typeParamsHeaders = ['Type', 'Mandatory', 'Sentiment-enabled', 'Multi-value'];
  const formattedTypeParamsData = [typeParamsHeaders];

  // Extract the unique types and their parameters (Mandatory, Sentiment-enabled, Multi-value)
  Object.entries(labelTypes).forEach(([type, params]) => {
    if (!['Gender', 'Age', 'Sentiment', 'Sources'].includes(type) && params) { // Exclude Gender, Age, Sentiment
      const row = [
        type,
        params.mandatory === true ? 'true' : 'false', // Keep true/false as strings
        params.sentimentEnabled === true ? 'true' : 'false',
        params.multiValue === true ? 'true' : 'false',
      ];
      formattedTypeParamsData.push(row);
    }
  });

  // Create workbook and append both sheets
  const wb = XLSX.utils.book_new();

  // Append Labels sheet
  const wsLabels = XLSX.utils.aoa_to_sheet(formattedLabelsData);
  XLSX.utils.book_append_sheet(wb, wsLabels, "Labels");

  // Append Type Parameters sheet
  const wsTypeParams = XLSX.utils.aoa_to_sheet(formattedTypeParamsData);
  XLSX.utils.book_append_sheet(wb, wsTypeParams, "Type Parameters");

  // Write the workbook to a file
  XLSX.writeFile(wb, filename);
};

export const loadFromExcel = (data, setLabels, setLabelTypes, defaultLabels, defaultLabelTypes) => {
  const workbook = XLSX.read(data, { type: 'array' });

  // Load "Labels" sheet
  const labelsSheet = workbook.Sheets["Labels"];
  const labelsData = XLSX.utils.sheet_to_json(labelsSheet, { header: 1 });

  // Load "Type Parameters" sheet
  const typeParamsSheet = workbook.Sheets["Type Parameters"];
  const typeParamsData = XLSX.utils.sheet_to_json(typeParamsSheet, { header: 1 });

  let labels = {};
  let labelTypes = {};

  // Process "Labels" sheet
  labelsData.slice(1).forEach((row) => {
    const [labelName, labelType, keywordsColumn, explanationColumn] = row;
    const labelTypeTrimmed = labelType ? labelType.trim() : '';
    const labelNameTrimmed = labelName ? labelName.trim() : '';

    if (labelNameTrimmed && labelTypeTrimmed) {
      // Ensure labelType exists in labels
      labels[labelTypeTrimmed] = labels[labelTypeTrimmed] || [];

      // Check if the label already exists in this label type
      const labelExists = labels[labelTypeTrimmed].some(label => label.name === labelNameTrimmed);

      // Only add the label if it doesn't already exist
      if (!labelExists) {
        // Process keywords as an array if provided
        const keywords = keywordsColumn ? keywordsColumn.split(/[,;]/).map((kw) => kw.trim()).filter(Boolean) : [];

        // Add the label with keywords and optional explanation
        labels[labelTypeTrimmed].push({
          name: labelNameTrimmed,
          keywords: keywords,
          explanation: explanationColumn ? explanationColumn.trim() : '', // Optional explanation
        });
      }

      // Initialize type in labelTypes if not already present
      if (!labelTypes[labelTypeTrimmed]) {
        labelTypes[labelTypeTrimmed] = {
          mandatory: false,
          sentimentEnabled: false,
          multiValue: false,
        };
      }
    }
  });

  // Process "Type Parameters" sheet
  typeParamsData.slice(1).forEach((row) => {
    const [type, mandatory, sentimentEnabled, multiValue] = row;
    const trimmedType = type ? type.trim() : ''; // Null-proof trim
    const mandatoryTrimmed = mandatory ? mandatory.trim() : '';
    const sentimentEnabledTrimmed = sentimentEnabled ? sentimentEnabled.trim() : '';
    const multiValueTrimmed = multiValue ? multiValue.trim() : '';

    if (labelTypes[trimmedType]) {
      // Check for existing label type and update only if necessary
      labelTypes[trimmedType].mandatory = mandatoryTrimmed === 'true' ? true : labelTypes[trimmedType].mandatory;
      labelTypes[trimmedType].sentimentEnabled = sentimentEnabledTrimmed === 'true' ? true : labelTypes[trimmedType].sentimentEnabled;
      labelTypes[trimmedType].multiValue = multiValueTrimmed === 'true' ? true : labelTypes[trimmedType].multiValue;
    }
  });

  // Merge loaded labels and labelTypes with the defaults
  labels = { ...defaultLabels, ...labels };
  labelTypes = { ...defaultLabelTypes, ...labelTypes };
  
  // Set the labels and labelTypes in the SetupContext
  setLabels(labels);
  setLabelTypes(labelTypes);
};

export const exportVerbatimsToXLSX = (verbatims, labels, labelTypes) => {
  const wb = XLSX.utils.book_new(); // Workbook initialization

  // Utility function to create and append sheets
  const appendSheet = (data, sheetName) => {
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheetName));
  };

  // Helper function to collect sentiment and non-sentiment labels
  const getLabelsByType = () => {
    const sentimentEnabledLabels = new Set();
    const allLabels = new Set();

    Object.keys(labelTypes).forEach(labelType => {
      if (!['Gender', 'Age', 'Sentiment'].includes(labelType)) {
        if (labelTypes?.[labelType]?.sentimentEnabled) {
          (labels[labelType] || []).forEach(label => sentimentEnabledLabels.add(label.name));
        } else {
          (labels[labelType] || []).forEach(label => allLabels.add(label.name));
        }
      }
    });
    return { sentimentEnabledLabels, allLabels };
  };

  // 1. Create "Verbatims By Type" Sheet
  const createVerbatimsByTypeSheet = () => {
    const { sentimentEnabledLabels, allLabels } = getLabelsByType();
    const sentimentEnabledTypes = new Set();
    const sentimentColumns = [];
    const otherColumns = [];

    Object.keys(labelTypes).forEach(type => {
      const labelTypeData = labelTypes[type];
      if (type !== 'Sentiment' && labelTypeData && labelTypeData?.sentimentEnabled) {
        sentimentEnabledTypes.add(type);
        sentimentColumns.push(`${type} (Positive)`, `${type} (Negative)`, `${type} (Mixed)`);
      } else {
        otherColumns.push(type);
      }
    });

    const headers = ['ID', 'Verbatim', 'Translation', 'Link', ...sentimentColumns, ...otherColumns];
    const dataSheet = [headers];

    verbatims.forEach(item => {
      const row = [item.id, item.verbatim, item.translation || '', item.link || ''];
      const sentimentValues = {};
      sentimentEnabledTypes.forEach(type => {
        sentimentValues[type] = { positive: [], negative: [], mixed: [] };
      });
      const otherValues = {};
      otherColumns.forEach(type => otherValues[type] = []);

      Object.entries(item.labels || {}).forEach(([labelType, labelsArray]) => {
        labelsArray
          .filter((label) => label && label.name) // Filter out null, undefined, or invalid labels
          .forEach(({ name, state }) => {
            if (sentimentEnabledTypes.has(labelType)) {
              // Initialize arrays for positive, negative, and mixed if they don't exist yet
              if (!sentimentValues[labelType]) {
                sentimentValues[labelType] = { positive: [], negative: [], mixed: [] };
              }

              // Push values into the appropriate sentiment array
              if (state === 'positive') {
                sentimentValues[labelType].positive.push(name);
              } else if (state === 'negative') {
                sentimentValues[labelType].negative.push(name);
              } else if (state === 'mixed') {
                sentimentValues[labelType].mixed.push(name);
              }
            } else {
              // Initialize the array for otherValues if it doesn't exist yet
              if (!otherValues[labelType]) {
                otherValues[labelType] = [];
              }

              // Push the label into the non-sentiment values array
              otherValues[labelType].push(name);
            }
          });
      });

      sentimentEnabledTypes.forEach(type => {
        row.push(
          sentimentValues[type].positive.join('; '),
          sentimentValues[type].negative.join('; '),
          sentimentValues[type].mixed.join('; ')
        );
      });

      otherColumns.forEach(type => row.push(otherValues[type].join('; ')));
      dataSheet.push(row);
    });

    appendSheet(dataSheet, "Verbatims By Type");
  };

  // 2. Create "Verbatims Quantified" Sheet
  const createVerbatimsQuantifiedSheet = () => {
    const { sentimentEnabledLabels, allLabels } = getLabelsByType();
    const quantHeaders = [
      'ID', 'Verbatim', 'Translation', 'Link', 'Gender', 'Age', 'Sentiment',
      ...Array.from(sentimentEnabledLabels).flatMap(label => [`${label} (Positive)`, `${label} (Negative)`, `${label} (Mixed)`]),
      ...Array.from(allLabels)
    ];

    const quantSheet = [quantHeaders];

    verbatims.forEach(item => {
      const row = [
        item.id, item.verbatim, item.translation || '', item.link || '',
        (item.labels.Gender && item.labels.Gender[0]?.name) || '',
        (item.labels.Age && item.labels.Age[0]?.name) || '',
        (item.labels.Sentiment && item.labels.Sentiment[0]?.name) || ''
      ];

      const labelPresence = {};
      Array.from(sentimentEnabledLabels).forEach(label => {
        labelPresence[`${label} (Positive)`] = 0;
        labelPresence[`${label} (Negative)`] = 0;
        labelPresence[`${label} (Mixed)`] = 0;
      });
      Array.from(allLabels).forEach(label => {
        labelPresence[label] = 0;
      });

      Object.entries(item.labels || {}).forEach(([labelType, labelsArray]) => {
        labelsArray
          .filter((label) => label && label.name) // Filter out null, undefined, or invalid labels
          .forEach(({ name, state }) => {
            if (sentimentEnabledLabels.has(name)) {
              if (state === 'positive') {
                labelPresence[`${name} (Positive)`] = 1;
              } else if (state === 'negative') {
                labelPresence[`${name} (Negative)`] = 1;
              } else if (state === 'mixed') {
                labelPresence[`${name} (Mixed)`] = 1;
              }
            } else {
              labelPresence[name] = 1;
            }
          });
      });


      Array.from(sentimentEnabledLabels).flatMap(label => [`${label} (Positive)`, `${label} (Negative)`, `${label} (Mixed)`])
        .forEach(label => row.push(labelPresence[label]));
      Array.from(allLabels).forEach(label => row.push(labelPresence[label]));

      quantSheet.push(row);
    });

    appendSheet(quantSheet, "Verbatims Quantified");
  };

  // 3. Create "Verbatim Label Counts" Sheet
  const createLabelCountsSheet = () => {
    const countSheet = [];

    const addLabelTypeSection = (labelType, labels, isSentimentEnabled) => {
      if (isSentimentEnabled) {
        countSheet.push([`${labelType} (Sentiment-Enabled)`]);
        countSheet.push(['Label', 'Positive', 'Negative', 'Mixed', 'Total']);
      } else {
        countSheet.push([`${labelType}`]);
        countSheet.push(['Label', 'Count']);
      }

      labels?.forEach(label => {
        let positiveCount = 0, negativeCount = 0, mixedCount = 0, count = 0;

        verbatims.forEach(item => {
          (item.labels[labelType] || [])
            .filter((label) => label && label.name) // Filter out invalid or null labels
            .forEach(({ name, state }) => {
              if (name === label) {
                if (isSentimentEnabled) {
                  if (state === 'positive') positiveCount++;
                  else if (state === 'negative') negativeCount++;
                  else if (state === 'mixed') mixedCount++;
                } else {
                  count++;
                }
              }
            });
        });


        if (isSentimentEnabled) {
          const total = positiveCount + negativeCount + mixedCount;
          countSheet.push([label, positiveCount, negativeCount, mixedCount, total]);
        } else {
          countSheet.push([label, count]);
        }
      });
      countSheet.push([]);
    };

    Object.keys(labelTypes).forEach(labelType => {
      const isSentimentEnabled = labelTypes?.[labelType]?.sentimentEnabled || false;
      const labelsArray = labels[labelType]?.map(l => l.name);
      addLabelTypeSection(labelType, labelsArray, isSentimentEnabled);
    });

    countSheet.push(['Sentiment']);
    countSheet.push(['Label', 'Positive', 'Negative', 'Mixed', 'Total']);

    let sentimentPositiveCount = 0, sentimentNegativeCount = 0, sentimentMixedCount = 0;

    verbatims.forEach(item => {
      item.labels.Sentiment?.forEach(({ state }) => {
        if (state === 'Positive') sentimentPositiveCount++;
        else if (state === 'Negative') sentimentNegativeCount++;
        else if (state === 'Mixed') sentimentMixedCount++;
      });
    });

    const sentimentTotal = sentimentPositiveCount + sentimentNegativeCount + sentimentMixedCount;
    countSheet.push(['Sentiment', sentimentPositiveCount, sentimentNegativeCount, sentimentMixedCount, sentimentTotal]);
    countSheet.push([]);

    appendSheet(countSheet, "Verbatim Label Counts");
  };

  // 4. Create "Labels" and "Type Parameters" Sheets
  const createLabelsAndTypeSheets = () => {
    const labelsHeaders = ['Label', 'Type', 'Keywords'];
    const formattedLabelsData = [labelsHeaders];
    Object.entries(labels).forEach(([label, dataObj]) => {
      if (Array.isArray(dataObj)) {
        dataObj.forEach(labelObj => {
          const labelName = labelObj.name || label;
          const keywords = Array.isArray(labelObj.keywords) ? labelObj.keywords.join('; ') : '';
          const row = [labelName, labelObj.type || label, keywords];
          formattedLabelsData.push(row);
        });
      } else {
        const keywords = Array.isArray(dataObj.keywords) ? dataObj.keywords.join('; ') : '';
        const row = [label, dataObj.type, keywords];
        formattedLabelsData.push(row);
      }
    });

    const typeParamsHeaders = ['Type', 'Mandatory', 'Sentiment-enabled', 'Multi-value'];
    const formattedTypeParamsData = [typeParamsHeaders];
    Object.entries(labelTypes).forEach(([type, params]) => {
      if (params) {
        const row = [
          type,
          params.mandatory ? 'true' : 'false',
          params.sentimentEnabled ? 'true' : 'false',
          params.multiValue ? 'true' : 'false'
        ];
        formattedTypeParamsData.push(row);
      }
    });

    appendSheet(formattedLabelsData, "Labels");
    appendSheet(formattedTypeParamsData, "Type Parameters");
  };

  // Generate all sheets
  createVerbatimsByTypeSheet();
  createVerbatimsQuantifiedSheet();
  createLabelCountsSheet();
  createLabelsAndTypeSheets();

  // Export the Excel file
  XLSX.writeFile(wb, "verbatims.xlsx");
};

export const loadVerbatimsFromExcel = (data) => {
	const workbook = XLSX.read(data, { type: 'array' });
	const verbatimsSheetName = workbook.SheetNames[0];
	const verbatimsSheet = verbatimsSheetName ? workbook.Sheets[verbatimsSheetName] : null;
	const jsonData = verbatimsSheet
		? XLSX.utils.sheet_to_json(verbatimsSheet, { header: 1, defval: '' })
		: [];
	const labelsSheet = workbook.Sheets.Labels;
	const typeParamsSheet = workbook.Sheets['Type Parameters'];
	const labelsData = labelsSheet
		? XLSX.utils.sheet_to_json(labelsSheet, { header: 1, defval: '' })
		: [];
	const typeParamsData = typeParamsSheet
		? XLSX.utils.sheet_to_json(typeParamsSheet, { header: 1, defval: '' })
		: [];
	const labelsWithTypes = {};
	const labelTypes = {};

	labelsData.slice(1).forEach((row) => {
		const labelName = toExcelString(row[0]);
		const labelType = toExcelString(row[1]);
		if (!labelName || !labelType) return;

		if (!labelsWithTypes[labelType]) {
			labelsWithTypes[labelType] = [];
		}

		const keywords = row
			.slice(2)
			.flatMap((value) => toExcelString(value).split(';'))
			.map((keyword) => keyword.trim())
			.filter(Boolean);

		if (!labelsWithTypes[labelType].some((label) => label.name === labelName)) {
			labelsWithTypes[labelType].push({
				name: labelName,
				keywords,
				explanation: '',
			});
		}

		if (!labelTypes[labelType]) {
			labelTypes[labelType] = {
				mandatory: false,
				sentimentEnabled: false,
				multiValue: false,
			};
		}
	});

	typeParamsData.slice(1).forEach((row) => {
		const type = toExcelString(row[0]);
		if (!type) return;

		const currentOptions = labelTypes[type] || {};
		labelTypes[type] = {
			mandatory: toExcelBoolean(row[1], currentOptions.mandatory || false),
			sentimentEnabled: toExcelBoolean(row[2], currentOptions.sentimentEnabled || false),
			multiValue: toExcelBoolean(row[3], currentOptions.multiValue || false),
		};
	});

	const headers = Array.isArray(jsonData[0]) ? jsonData[0] : [];
	const idIndex = findHeaderIndex(headers, 'ID');
	const verbatimIndex = findHeaderIndex(headers, 'Verbatim');
	const translationIndex = findHeaderIndex(headers, 'Translation');
	const imageIndex = findHeaderIndex(headers, 'Verbatim(Image)');
	const linkIndex = findHeaderIndex(headers, 'Link');
	const labelsStartIndex = Math.max(idIndex, verbatimIndex, translationIndex, imageIndex, linkIndex) + 1;
	const rowsWithSources = jsonData
		.slice(1)
		.map((row, index) => ({ row, sourceRow: index + 2 }))
		.filter(({ row }) => !isEmptyExcelRow(row));

	const verbatims = rowsWithSources.map(({ row }, rowIndex) => {
		const textValue = verbatimIndex >= 0 ? row[verbatimIndex] : '';
		const imageValue = imageIndex >= 0 ? toExcelString(row[imageIndex]) : '';
		const textAsString = toExcelString(textValue);
		let verbatim = textAsString || textValue;

		if (imageValue) {
			verbatim = [
				...(textAsString ? [{ type: 'text', content: textAsString }] : []),
				{ type: 'image', content: imageValue },
			];
		}

		const labels = {};

		headers.slice(labelsStartIndex).forEach((header, index) => {
			const labelHeader = toExcelString(header);
			const columnValue = toExcelString(row[labelsStartIndex + index]);
			if (!labelHeader || !columnValue) return;

			if (normalizeColumnName(labelHeader) === 'sentiment') {
				const sentimentValue = columnValue.toLowerCase();
				labels.Sentiment = [{ name: sentimentValue, state: sentimentValue, type: 'Sentiment' }];
				return;
			}

			const match = labelHeader.match(/(.+?) \((Positive|Negative|Mixed|Neutral)\)/i);
			const labelType = (match ? match[1] : labelHeader).trim();
			const sentimentState = match ? match[2].toLowerCase() : null;
			const attributeValues = columnValue
				.split(';')
				.map((attribute) => attribute.trim())
				.filter(Boolean);

			attributeValues.forEach((attribute) => {
				const isSentimentEnabled = labelTypes?.[labelType]?.sentimentEnabled || false;
				const state = isSentimentEnabled && sentimentState ? sentimentState : 'active';

				if (!labels[labelType]) {
					labels[labelType] = [];
				}

				labels[labelType].push({ name: attribute, state, type: labelType });
			});
		});

		return {
			id: idIndex >= 0 ? row[idIndex] : rowIndex + 1,
			verbatim,
			...(translationIndex >= 0 ? { translation: '' } : {}),
			link: linkIndex >= 0 ? row[linkIndex] : '',
			labels,
		};
	});

	return {
		verbatims,
		labels: labelsWithTypes,
		labelTypes,
		sourceRows: rowsWithSources.map(({ sourceRow }) => sourceRow),
	};
};

export const loadVerbatimsFromRadarlyExcel = (excelData, selectedColumns, importOptions = {}) => {
	const headers = Array.isArray(excelData?.[0]) ? excelData[0] : [];
	const hasTranslationColumn = findHeaderIndex(headers, 'Translation') >= 0;
	const activeFormat = importOptions.format || RADARLY_FORMATS[0];
	const headerRowNumber = Number.isInteger(importOptions.headerRowNumber)
		? importOptions.headerRowNumber
		: 5;
	const rowsWithSources = (Array.isArray(excelData) ? excelData.slice(1) : [])
		.map((row, index) => ({ row, sourceRow: headerRowNumber + index + 1 }))
		.filter(({ row }) => !isEmptyExcelRow(row));
	const labelsWithTypes = {
		Gender: [],
		Age: [{ name: 'Unknown Age', keywords: [], explanation: '' }],
		Sentiment: [],
	};
	const labelTypes = {
		Gender: { mandatory: true, sentimentEnabled: false, multiValue: false },
		Age: { mandatory: true, sentimentEnabled: false, multiValue: false },
		Sentiment: { mandatory: true, sentimentEnabled: true, multiValue: false },
	};
	const usableColumns = (Array.isArray(selectedColumns) ? selectedColumns : [])
		.filter((column) => !isRadarlyReservedColumn(column, activeFormat));

	const verbatims = rowsWithSources.map(({ row }, index) => {
		const rowData = headers.reduce((acc, header, headerIndex) => {
			acc[normalizeColumnName(header)] = row[headerIndex];
			return acc;
		}, {});
		const contentValue = activeFormat.contentColumns
			.map((column) => rowData[column])
			.find((value) => toExcelString(value) !== '');
		const verbatim = toExcelString(contentValue) || contentValue || '';
		const link = rowData[activeFormat.linkColumn] ?? '';
		const rawGender = toExcelString(rowData.gender).toUpperCase();
		const gender = rawGender === 'F' ? 'Woman' : rawGender === 'M' ? 'Man' : 'Unknown Gender';
		const sentiment = (
			toExcelString(rowData[activeFormat.sentimentColumn]) || 'neutral'
		).toLowerCase();
		const labels = {
			Gender: [{ name: gender, state: 'active', type: 'Gender' }],
			Age: [{ name: 'Unknown Age', state: 'active', type: 'Age' }],
			Sentiment: [{ name: sentiment, state: sentiment, type: 'Sentiment' }],
		};

		if (!labelsWithTypes.Gender.some((label) => label.name === gender)) {
			labelsWithTypes.Gender.push({ name: gender, keywords: [], explanation: '' });
		}
		if (!labelsWithTypes.Sentiment.some((label) => label.name === sentiment)) {
			labelsWithTypes.Sentiment.push({ name: sentiment, keywords: [], explanation: '' });
		}

		usableColumns.forEach((column) => {
			const labelType = toExcelString(column);
			const cellValue = toExcelString(rowData[normalizeColumnName(column)]);
			if (!labelType || !cellValue) return;

			const labelValues = cellValue
				.split(';')
				.map((value) => value.trim())
				.filter(Boolean);
			if (labelValues.length === 0) return;

			if (!labels[labelType]) {
				labels[labelType] = [];
			}
			if (!labelsWithTypes[labelType]) {
				labelsWithTypes[labelType] = [];
			}
			if (!labelTypes[labelType]) {
				labelTypes[labelType] = {
					mandatory: false,
					sentimentEnabled: false,
					multiValue: true,
				};
			}

			labelValues.forEach((labelValue) => {
				labels[labelType].push({
					name: labelValue,
					state: 'active',
					type: labelType,
				});

				if (!labelsWithTypes[labelType].some((label) => label.name === labelValue)) {
					labelsWithTypes[labelType].push({
						name: labelValue,
						keywords: [],
						explanation: '',
					});
				}
			});
		});

		return {
			id: index + 1,
			verbatim,
			...(hasTranslationColumn ? { translation: '' } : {}),
			link,
			labels,
		};
	});

	return {
		verbatims,
		labels: labelsWithTypes,
		labelTypes,
		sourceRows: rowsWithSources.map(({ sourceRow }) => sourceRow),
	};
};

export const exportTableToExcel = (
  pivotData,
  rowLabels,
  colLabels,
  selectedRowLabel,
  selectedColLabel,
  fileName = "PivotTable.xlsx",
  isPercentage = false,
  sampleSize = 0,
  displayMode = "raw"
) => {
  if (!pivotData || rowLabels.length === 0 || colLabels.length === 0) {
    console.error("No data available to export.");
    return;
  }

  // Prepare metadata rows
  const metadata = [
    ["Pivot Table Metadata"], // Title row
    ["Sample Size", sampleSize],
    ["Calculation Mode", displayMode],
    ["Row Label Type", selectedRowLabel],
    ["Column Label Type", selectedColLabel],
    [], // Blank row for separation
  ];

  // Prepare the table header
  const tableHeader = [`${selectedRowLabel} / ${selectedColLabel}`, ...colLabels];

  // Prepare the table data
  const tableData = rowLabels.map((rowLabel) => {
    const row = [rowLabel];
    colLabels.forEach((colLabel) => {
      let value = pivotData[rowLabel]?.[colLabel] || 0;

      if (isPercentage) {
        value = typeof value === "number" ? `${value.toFixed(2)}%` : value; // Ensure consistent percentage formatting
      }

      row.push(value);
    });
    return row;
  });

  // Combine metadata and table data
  const worksheetData = [...metadata, tableHeader, ...tableData];

  // Create a worksheet and workbook
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Pivot Table");

  // Export the Excel file
  XLSX.writeFile(workbook, fileName);
};


/**
 * Export verbatims to ExcelJS format
 * It creates a worksheet with verbatims organized by type, including text and image content.
 * 
 * @param {Array} verbatims - Array of verbatim objects
 * @param {Object} labels - Labels 
 * @param {Object} labelTypes - Types of labels
 */
export const exportVerbatimsToExcelJS = async (verbatims, labels, labelTypes) => {
  const workbook = new ExcelJS.Workbook();

  /**
   * Resize the image to fit within the specified dimensions
   *
   * @param {string} base64Image
   * @param {number} maxWidth 
   * @param {number} maxHeight 
   * @returns {Promise<{ width: number, height: number }>} 
   */
  function getScaledDimensionsFromBase64(base64Image, maxWidth, maxHeight) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const originalWidth = img.width;
        const originalHeight = img.height;

        const widthRatio = maxWidth / originalWidth;
        const heightRatio = maxHeight / originalHeight;
        const scale = Math.min(widthRatio, heightRatio, 1);

        const width = Math.round(originalWidth * scale);
        const height = Math.round(originalHeight * scale);

        resolve({ width, height });
      };

      img.onerror = (err) => {
        reject(new Error("Impossible de charger l'image base64"));
      };

      img.src = base64Image;
    });
  }

    /**
   * Prepare image sizes for verbatims
   * @param {Array} verbatims
   * @param {number} maxWidth
   * @param {number} maxHeight
   * @returns {Promise<Record<string, { width: number, height: number }>>}
   */
  async function prepareImageSizes(verbatims, maxWidth = 200, maxHeight = 150) {
    const result = {};

    for (const item of verbatims) {
      if (Array.isArray(item.verbatim)) {
        const imageItems = item.verbatim.filter(v => v.type === 'image' && v.content);
        if (imageItems.length > 0) {
          try {
            const imageData = base64ToImage(imageItems[0].content);
            const { width, height } = await getScaledDimensionsFromBase64(imageData, maxWidth, maxHeight);
            result[item.id] = { width, height };
          } catch (error) {
            console.error('Erreur lors de la préparation de l\'image:', error);
          }
        }
      }
    }

    return result;
  }

  /**
   * Convert a base64 image string to an ArrayBuffer
   * @param {string} base64String
   * @returns {Object}
   */
  const base64ToArrayBuffer = (base64String) => {
    try {
      if (base64String && base64String.startsWith('data:image')) {
        const matches = base64String.match(/^data:(.+);base64,(.+)$/);
        if (matches && matches.length === 3) {
          const [, type, base64Data] = matches;
          const binaryString = window.atob(base64Data);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          return { extension: type.split('/')[1], data: bytes };
        }
      }
      return null;
    } catch (error) {
      console.error("Erreur lors de la conversion base64 en ArrayBuffer:", error);
      return null;
    }
  };

  /**
   * 1. Create a new worksheet for verbatims organized by type
   */
  const createVerbatimsByTypeSheet = async () => {
    const sheet = workbook.addWorksheet('Verbatims By Type');
    const sentimentEnabledTypes = new Set();
    const sentimentColumns = [];
    const otherColumns = [];

    Object.keys(labelTypes).forEach(type => {
      const labelTypeData = labelTypes[type];
      if (type !== 'Sentiment' && labelTypeData && labelTypeData?.sentimentEnabled) {
        sentimentEnabledTypes.add(type);
        sentimentColumns.push(`${type} (Positive)`, `${type} (Negative)`, `${type} (Mixed)`);
      } else {
        otherColumns.push(type);
      }
    });

    // Create headers for the worksheet
    const headers = ['ID', 'Verbatim', 'Translation', 'Verbatim(Image)', 'Link', ...sentimentColumns, ...otherColumns];
    sheet.addRow(headers);
    
    // Ajust column widths
    sheet.getColumn(1).width = 10;  // ID
    sheet.getColumn(2).width = 60;  // Text
    sheet.getColumn(3).width = 40;  // Translation
    sheet.getColumn(4).width = 40;  // Image
    sheet.getColumn(5).width = 30;  // Link
    
    for (let i = 5; i < headers.length + 1; i++) {
      sheet.getColumn(i).width = 20;
    }

    // Configure header row styles
    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Iterate through verbatims and add them to the worksheet
    let rowIndex = 2; // Start from the second row (first is header)  
    for (const item of verbatims) {
      const row = sheet.addRow();
      row.getCell(1).value = item.id;
      
      // Extract verbatim text and image
      if (typeof item.verbatim === 'string') {
        row.getCell(2).value = item.verbatim;
        row.getCell(3).value = item.translation || '';
        row.getCell(4).value = '';
      } else if (Array.isArray(item.verbatim)) {
        const textContent = item.verbatim
          .filter(v => v.type === 'text')
          .map(v => v.content)
          .join('\n');
          
        row.getCell(2).value = textContent;
        row.getCell(3).value = item.translation || '';
        const imageItems = item.verbatim.filter(v => v.type === 'image' && v.content);
        
        if (imageItems.length > 0) {
          try {
            row.getCell(4).value = "";
            // const imageData = base64ToImage(imageItems[0].content);
            // const imageInfo = base64ToArrayBuffer(imageData);
            
            // if (imageInfo) {
            //   // Get the image sizes for this verbatim
            //   const { width, height } = imageSizesById[item.id] || { width: 200, height: 150 };
            //   // Add the image to the workbook
            //   const imageId = workbook.addImage({
            //     buffer: imageInfo.data,
            //     extension: imageInfo.extension,
            //     width: width,
            //     height: height
            //   });
            //   // Position the image in the cell
            //   sheet.addImage(imageId, {
            //     tl: { col: 3, row: rowIndex - 1 },
            //     ext: { width: width, height: height },  // Dimensions explicites
            //     editAs: 'absolute'
            //   });
            //   // Ajust the row height to fit the image
            //   row.height = 150;
            // }
          } catch (error) {
            console.error('Erreur lors de l\'insertion de l\'image:', error);
          }
        } else {
          row.getCell(4).value = '';
        }
      }
      
      row.getCell(5).value = item.link || '';
      const sentimentValues = {};
      sentimentEnabledTypes.forEach(type => {
        sentimentValues[type] = { positive: [], negative: [], mixed: [] };
      });
      
      const otherValues = {};
      otherColumns.forEach(type => otherValues[type] = []);

      // Iterate through labels and categorize them
      Object.entries(item.labels || {}).forEach(([labelType, labelsArray]) => {
        labelsArray
          .filter((label) => label && label.name)
          .forEach(({ name, state }) => {
            if (sentimentEnabledTypes.has(labelType)) {
              if (!sentimentValues[labelType]) {
                sentimentValues[labelType] = { positive: [], negative: [], mixed: [] };
              }

              if (state === 'positive') {
                sentimentValues[labelType].positive.push(name);
              } else if (state === 'negative') {
                sentimentValues[labelType].negative.push(name);
              } else if (state === 'mixed') {
                sentimentValues[labelType].mixed.push(name);
              }
            } else {
              if (!otherValues[labelType]) {
                otherValues[labelType] = [];
              }

              otherValues[labelType].push(name);
            }
          });
      });

      let columnIndex = 6; // Start after ID, Verbatim(Text), Translation, Verbatim(Image), and Link columns
      sentimentEnabledTypes.forEach(type => {
        row.getCell(columnIndex++).value = sentimentValues[type].positive.join('; ');
        row.getCell(columnIndex++).value = sentimentValues[type].negative.join('; ');
        row.getCell(columnIndex++).value = sentimentValues[type].mixed.join('; ');
      });
      otherColumns.forEach(type => row.getCell(columnIndex++).value = otherValues[type].join('; '));
      rowIndex++;
    }
  };
  
  /**
   * 2. Create a new worksheet for verbatims quantified data
   */
  const createVerbatimsQuantifiedSheet = async () => {
    const sheet = workbook.addWorksheet('Verbatims Quantified');
    const sentimentEnabledLabels = new Set();
    const allLabels = new Set();

    Object.keys(labelTypes).forEach(labelType => {
      if (!['Gender', 'Age', 'Sentiment'].includes(labelType)) {
        if (labelTypes?.[labelType]?.sentimentEnabled && labels[labelType]) {
          labels[labelType].forEach(label => sentimentEnabledLabels.add(label.name));
        } else if (labels[labelType]) {
          labels[labelType].forEach(label => allLabels.add(label.name));
        }
      }
    });

    // Create headers for the worksheet
    const headers = [
      'ID', 'Verbatim', 'Translation', 'Verbatim(Image)', 'Link', 'Gender', 'Age', 'Sentiment',
      ...Array.from(sentimentEnabledLabels).flatMap(label => [
        `${label} (Positive)`, `${label} (Negative)`, `${label} (Mixed)`
      ]),
      ...Array.from(allLabels)
    ];
    
    sheet.addRow(headers);

    // Ajust column widths
    sheet.getColumn(1).width = 10;  // ID
    sheet.getColumn(2).width = 60;  // Text
    sheet.getColumn(3).width = 40;  // Translation
    sheet.getColumn(4).width = 40;  // Image
    sheet.getColumn(5).width = 30;  // Link
    sheet.getColumn(6).width = 15;  // Gender
    sheet.getColumn(7).width = 15;  // Age
    sheet.getColumn(8).width = 15;  // Sentiment
    
    // Style the header row
    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    let rowIndex = 2;
    for (const item of verbatims) {
      const row = sheet.addRow();
      row.getCell(1).value = item.id;
      
      // Check if verbatim is a string or an array
      if (typeof item.verbatim === 'string') {
        row.getCell(2).value = item.verbatim;
        row.getCell(3).value = item.translation || '';
        row.getCell(4).value = ''; 
      } else if (Array.isArray(item.verbatim)) {
        const textContent = item.verbatim
          .filter(v => v.type === 'text')
          .map(v => v.content)
          .join('\n');
          
        row.getCell(2).value = textContent;
        row.getCell(3).value = item.translation || '';
        const imageItems = item.verbatim.filter(v => v.type === 'image' && v.content);
        
        if (imageItems.length > 0) {
          try {
            row.getCell(4).value = "";
            const imageData = base64ToImage(imageItems[0].content);
            const imageInfo = base64ToArrayBuffer(imageData);
            
            if (imageInfo) {
              // Get the image sizes for this verbatim
              const { width, height } = imageSizesById[item.id] || { width: 200, height: 150 };
              // Add the image to the workbook
              const imageId = workbook.addImage({
                buffer: imageInfo.data,
                extension: imageInfo.extension,
                width: width, 
                height: height
              });
              // Position the image in the cell
              sheet.addImage(imageId, {
                tl: { col: 3, row: rowIndex - 1 },
                ext: { width: width, height: height },
                editAs: 'absolute'
              });
              // Ajust the row height to fit the image
              row.height = 150;
            }
          } catch (error) {
            console.error('Erreur lors de l\'insertion de l\'image:', error);
          }
        } else {
          row.getCell(4).value = '';
        }
      }
      
      row.getCell(5).value = item.link || '';
      row.getCell(6).value = (item.labels.Gender && item.labels.Gender[0]?.name) || '';
      row.getCell(7).value = (item.labels.Age && item.labels.Age[0]?.name) || '';
      row.getCell(8).value = (item.labels.Sentiment && item.labels.Sentiment[0]?.name) || '';

      // Prepare label presence data
      const labelPresence = {};
      Array.from(sentimentEnabledLabels).forEach(label => {
        labelPresence[`${label} (Positive)`] = 0;
        labelPresence[`${label} (Negative)`] = 0;
        labelPresence[`${label} (Mixed)`] = 0;
      });
      Array.from(allLabels).forEach(label => {
        labelPresence[label] = 0;
      });

      Object.entries(item.labels || {}).forEach(([labelType, labelsArray]) => {
        labelsArray
          .filter((label) => label && label.name)
          .forEach(({ name, state }) => {
            if (sentimentEnabledLabels.has(name)) {
              if (state === 'positive') {
                labelPresence[`${name} (Positive)`] = 1;
              } else if (state === 'negative') {
                labelPresence[`${name} (Negative)`] = 1;
              } else if (state === 'mixed') {
                labelPresence[`${name} (Mixed)`] = 1;
              }
            } else {
              labelPresence[name] = 1;
            }
          });
      });

      let colIndex = 9; // start after ID, Verbatim(Text), Translation, Verbatim(Image), Link
      Array.from(sentimentEnabledLabels).flatMap(label => [
        `${label} (Positive)`, `${label} (Negative)`, `${label} (Mixed)`
      ]).forEach(label => {
        row.getCell(colIndex++).value = labelPresence[label];
      });
      
      Array.from(allLabels).forEach(label => {
        row.getCell(colIndex++).value = labelPresence[label];
      });
      
      rowIndex++;
    }
  };

  /**
   * 3. Create a new worksheet for verbatim label counts
   */
  const createLabelCountsSheet = () => {
    const sheet = workbook.addWorksheet('Verbatim Label Counts');

    const addLabelTypeSection = (labelType, labelsList, isSentimentEnabled) => {
      if (isSentimentEnabled) {
        sheet.addRow([`${labelType} (Sentiment-Enabled)`]);
        sheet.addRow(['Label', 'Positive', 'Negative', 'Mixed', 'Total']);
      } else {
        sheet.addRow([`${labelType}`]);
        sheet.addRow(['Label', 'Count']);
      }

      labelsList.forEach(label => {
        let positiveCount = 0, negativeCount = 0, mixedCount = 0, count = 0;

        verbatims.forEach(item => {
          (item.labels[labelType] || [])
            .filter(l => l && l.name)
            .forEach(({ name, state }) => {
              if (name === label) {
                if (isSentimentEnabled) {
                  if (state === 'positive') positiveCount++;
                  else if (state === 'negative') negativeCount++;
                  else if (state === 'mixed') mixedCount++;
                } else {
                  count++;
                }
              }
            });
        });

        if (isSentimentEnabled) {
          const total = positiveCount + negativeCount + mixedCount;
          sheet.addRow([label, positiveCount, negativeCount, mixedCount, total]);
        } else {
          sheet.addRow([label, count]);
        }
      });
      sheet.addRow([]);
    };

    Object.keys(labelTypes).forEach(labelType => {
      if (labels[labelType]) {
        const isSentimentEnabled = labelTypes[labelType]?.sentimentEnabled || false;
        const labelsArray = labels[labelType].map(l => l.name);
        addLabelTypeSection(labelType, labelsArray, isSentimentEnabled);
      }
    });

    sheet.addRow(['Sentiment']);
    sheet.addRow(['Label', 'Positive', 'Negative', 'Mixed', 'Total']);

    let sentimentPositiveCount = 0, sentimentNegativeCount = 0, sentimentMixedCount = 0;
    verbatims.forEach(item => {
      item.labels.Sentiment?.forEach(({ state }) => {
        if (state === 'Positive') sentimentPositiveCount++;
        else if (state === 'Negative') sentimentNegativeCount++;
        else if (state === 'Mixed') sentimentMixedCount++;
      });
    });

    const sentimentTotal = sentimentPositiveCount + sentimentNegativeCount + sentimentMixedCount;
    sheet.addRow(['Sentiment', sentimentPositiveCount, sentimentNegativeCount, sentimentMixedCount, sentimentTotal]);
    sheet.addRow([]);
    sheet.getColumn(1).width = 30;
    sheet.getColumn(2).width = 15;
    sheet.columns.forEach(column => {
      if (column.number > 2) {
        column.width = 15;
      }
    });
  };
  
  /**
   * 4. Create "Labels" and "Type Parameters" sheets
   */
  const createLabelsAndTypeSheets = () => {
    const labelsSheet = workbook.addWorksheet('Labels');
    labelsSheet.addRow(['Label', 'Type', 'Keywords']);
    
    Object.entries(labels).forEach(([labelType, dataArray]) => {
      if (Array.isArray(dataArray)) {
        dataArray.forEach(labelObj => {
          const labelName = labelObj.name || labelType;
          const keywords = Array.isArray(labelObj.keywords) ? labelObj.keywords.join('; ') : '';
          labelsSheet.addRow([labelName, labelObj.type || labelType, keywords]);
        });
      }
    });
    
    labelsSheet.getColumn(1).width = 30;
    labelsSheet.getColumn(2).width = 20;
    labelsSheet.getColumn(3).width = 50;
    
    const labelsHeaderRow = labelsSheet.getRow(1);
    labelsHeaderRow.font = { bold: true };
    labelsHeaderRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
    
    const typeParamsSheet = workbook.addWorksheet('Type Parameters');
    typeParamsSheet.addRow(['Type', 'Mandatory', 'Sentiment-enabled', 'Multi-value']);
    
    Object.entries(labelTypes).forEach(([type, params]) => {
      if (params) {
        typeParamsSheet.addRow([
          type,
          params.mandatory ? 'true' : 'false',
          params.sentimentEnabled ? 'true' : 'false',
          params.multiValue ? 'true' : 'false'
        ]);
      }
    });
    
    typeParamsSheet.getColumn(1).width = 30;
    typeParamsSheet.getColumn(2).width = 15;
    typeParamsSheet.getColumn(3).width = 20;
    typeParamsSheet.getColumn(4).width = 15;
    
    const typeHeaderRow = typeParamsSheet.getRow(1);
    typeHeaderRow.font = { bold: true };
    typeHeaderRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };
  };

  // Prepare image sizes for verbatims
  const imageSizesById = await prepareImageSizes(verbatims);
  // Generate all sheets
  await createVerbatimsByTypeSheet();
  createVerbatimsQuantifiedSheet();
  createLabelCountsSheet();
  createLabelsAndTypeSheets();
  
  // Save the workbook to a buffer and create a downloadable file
  await workbook.xlsx.writeBuffer().then(buffer => {
    // Create a Blob from the buffer
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
    // Create a link to download the Blob
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = "verbatims_with_images.xlsx";
    a.click();
    // Clean up the URL object
    window.URL.revokeObjectURL(url);
  });
};
